import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  Chip,
  Stack,
} from '@mui/material';
import { Save as SaveIcon, Refresh as RefreshIcon, ContentCopy as CopyIcon } from '@mui/icons-material';
import { promptService, GlobalPromptResponse } from '../services/promptService';
import { typography } from '~/theme';

interface GlobalPromptEditorProps {
  locale?: string;
  onLocaleCreated?: (locale: string) => void;
}

export const GlobalPromptEditor: React.FC<GlobalPromptEditorProps> = ({ locale, onLocaleCreated }) => {
  const [promptData, setPromptData] = useState<GlobalPromptResponse | null>(null);
  const [editedPrompt, setEditedPrompt] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [hasChanges, setHasChanges] = useState(false);

  const isNonDefaultLocale = locale && locale !== 'default';

  const loadPrompt = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await promptService.getGlobalPrompt(locale);
      setPromptData(data);
      setEditedPrompt(data.prompt_text);
      setHasChanges(false);
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { detail?: string } } };
      setError(axiosErr.response?.data?.detail || 'Failed to load global prompt');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPrompt();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locale]);

  const handlePromptChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setEditedPrompt(event.target.value);
    setHasChanges(event.target.value !== promptData?.prompt_text);
    setSuccess(null);
  };

  const handleSave = async () => {
    if (!editedPrompt.trim() || editedPrompt.trim().length < 50) {
      setError('Prompt must be at least 50 characters');
      return;
    }
    try {
      setSaving(true);
      setError(null);
      const result = await promptService.updateGlobalPrompt(editedPrompt, locale);
      setSuccess(result.message);
      setHasChanges(false);
      await loadPrompt();
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { detail?: string } } };
      setError(axiosErr.response?.data?.detail || 'Failed to save global prompt');
    } finally {
      setSaving(false);
    }
  };

  const handleCopyAndCreate = async () => {
    if (!locale) return;
    try {
      setSaving(true);
      setError(null);
      await promptService.createLocale(locale, editedPrompt);
      setSuccess(`Locale-specific prompt created for ${locale}. You can now edit and save it.`);
      setHasChanges(false);
      await loadPrompt();
      onLocaleCreated?.(locale);
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { detail?: string } } };
      setError(axiosErr.response?.data?.detail || 'Failed to create locale prompt');
    } finally {
      setSaving(false);
    }
  };

  const handleReload = () => {
    if (hasChanges && !window.confirm('You have unsaved changes. Reload and discard changes?')) return;
    loadPrompt();
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress />
      </Box>
    );
  }

  const isDefaultFallback = promptData?.is_default_fallback ?? false;

  return (
    <Card>
      <CardContent>
        <Stack spacing={2}>
          {/* Header */}
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="h6" gutterBottom>
                Global Report Generation Prompt
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Base prompt used for all report generation.
                {isNonDefaultLocale && ' CPT-specific guidance is appended per exam.'}
              </Typography>
            </Box>
            {hasChanges && <Chip label="Unsaved Changes" color="warning" size="small" />}
          </Box>

          {/* Default fallback banner */}
          {isDefaultFallback && isNonDefaultLocale && (
            <Alert
              severity="info"
              action={
                <Button
                  color="inherit"
                  size="small"
                  startIcon={<CopyIcon />}
                  onClick={handleCopyAndCreate}
                  disabled={saving}
                >
                  Copy & create locale file
                </Button>
              }
            >
              No locale-specific prompt found for <strong>{locale}</strong>. Showing the default prompt as a starting point.
              Use the button to create a locale-specific copy you can customise.
            </Alert>
          )}

          {/* File info chips */}
          {promptData && (
            <Box display="flex" gap={1} flexWrap="wrap">
              {promptData.is_locale_specific && (
                <Chip label={`Locale: ${locale}`} size="small" color="primary" variant="outlined" />
              )}
              <Chip label={`File: ${promptData.file_path}`} size="small" variant="outlined" />
              <Chip label={`${promptData.char_count.toLocaleString()} chars`} size="small" variant="outlined" />
            </Box>
          )}

          {/* Alerts */}
          {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
          {success && <Alert severity="success" onClose={() => setSuccess(null)}>{success}</Alert>}

          {/* Prompt Editor */}
          <TextField
            multiline
            rows={20}
            fullWidth
            value={editedPrompt}
            onChange={handlePromptChange}
            placeholder="Enter global prompt text..."
            variant="outlined"
            InputProps={{ style: { fontFamily: 'monospace', fontSize: typography.body2.fontSize } }}
          />

          <Typography variant="caption" color="text.secondary" align="right">
            {editedPrompt.length.toLocaleString()} characters
          </Typography>

          {/* Actions */}
          <Box display="flex" gap={2} justifyContent="flex-end">
            <Button variant="outlined" startIcon={<RefreshIcon />} onClick={handleReload} disabled={saving}>
              Reload
            </Button>
            {isDefaultFallback && isNonDefaultLocale ? (
              <Button
                variant="contained"
                startIcon={<CopyIcon />}
                onClick={handleCopyAndCreate}
                disabled={saving || editedPrompt.trim().length < 50}
              >
                {saving ? 'Creating...' : `Save as ${locale} prompt`}
              </Button>
            ) : (
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSave}
                disabled={!hasChanges || saving}
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            )}
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
};
